import React, { useState, useEffect } from 'react';
import { FaTimes, FaUserPlus, FaUserMinus } from 'react-icons/fa';
import { Dialog } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBroom, faTimes as faTimesIcon, faUserPlus as faUserPlusIcon } from '@fortawesome/free-solid-svg-icons';

import { TaskDTO } from '../project/task/TaskApi';
import { GeneralTaskReportDTO } from '../general-task/GeneralTaskApi';
import { User, searchUsersWithNames, UserSearchRequest } from '../admin/user/UserApi';
import { TaskAssignmentDTO, TaskAssignmentApi, TaskAssignmentFilterRequest } from './TaskAssignmentApi';

interface AssignedUserResponse extends User {
  assignmentId: string;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  task?: TaskDTO | null;
  generalTaks?: GeneralTaskReportDTO | null;
  entityId?: string;
  entityType?: string;
  entityName?: string;
  taskType?: string;
};

export const TaskAssignmentModal: React.FC<Props> = ({
  isOpen,
  onClose,
  task,
  generalTaks,
  entityId,
  entityType,
  taskType
}) => {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<AssignedUserResponse[]>([]);
  const [comments, setComments] = useState('');

  const loggedInUser = JSON.parse(localStorage.getItem('user') || '{}');
  const tenantId = loggedInUser?.tenantId;
  const tenantName = loggedInUser?.tenantName;
  const assignedBy = loggedInUser?.id;

  useEffect(() => {
    const fetchAssignedUsers = async () => {
      if (!tenantId || !entityId || !entityType) return;

      const request: TaskAssignmentFilterRequest = {
        tenantId,
        entityId,
        entityType
      };

      const apiResponse = await TaskAssignmentApi.getAssignedUsers(request);
      const res: AssignedUserResponse[] = apiResponse.map(user => ({
        ...user,
        id: user.userId || ''
      }));
      setSelectedUsers(res);
    };

    if (isOpen) {
      fetchAssignedUsers();
    }
  }, [isOpen, tenantId, entityId, entityType]);

  useEffect(() => {
    if (search.length < 2) {
      setUsers([]);
      return;
    }

    const delay = setTimeout(() => {
      const request: UserSearchRequest = { keyword: search, tenantId };
      searchUsersWithNames(request).then(setUsers);
    }, 300);

    return () => clearTimeout(delay);
  }, [search]);

  const isUserSelected = (user: User) => selectedUsers.some(u => u.id === user.id);

  const toggleUser = async (user: AssignedUserResponse) => {
    const existing = selectedUsers.find(u => u.id === user.id);
    if (existing) {
      if (existing.assignmentId) {
        if (window.confirm('Do you want to unassign this user from the task?')) {
          await TaskAssignmentApi.delete(existing.assignmentId);
          setSelectedUsers(prev => prev.filter(u => u.id !== user.id));
        }
      } else {
        setSelectedUsers(prev => prev.filter(u => u.id !== user.id));
      }
    } else {
      setSelectedUsers(prev => [...prev, { ...user, assignmentId: '' }]);
    }
  };

  const handleSubmit = async () => {
    const now = new Date().toISOString();
    const newAssignments = selectedUsers.filter(u => !u.assignmentId);

    const assignments: TaskAssignmentDTO[] = newAssignments.map(user => ({
      tenantId,
      tenantName,
      entityId,
      entityType,
      userId: user.id,
      comments,
      userName: user.username,
      status: 'ASSIGNED',
      role: 'ASSIGNEE', // Default role
      assignedBy,
      assignedAt: now,
      isActive: true,
      isDeleted: false,
      createdBy: assignedBy,
      createdAt: now
    }));

    if (assignments.length > 0) {
      await TaskAssignmentApi.bulkAssign(assignments);
    }

    onClose();
  };

  const handleClearSelection = () => {
    setSelectedUsers(prev => prev.filter(u => u.assignmentId));
  };

  const clearSearch = () => setSearch('');

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black opacity-30" />
        <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full z-50 p-6 relative">
          <button className="absolute top-3 right-3 text-gray-500 hover:text-red-500" onClick={onClose}>
            <FaTimes size={20} />
          </button>

          <Dialog.Title className="text-xl font-semibold text-gray-800 mb-4">Assign Users</Dialog.Title>

          {(task || generalTaks) && (
            <div className="mb-4">
              <p className="text-gray-700">
                <span className="font-medium">Entity Name:</span> {task?.title || generalTaks?.title}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Entity Type:</span> {entityType}
              </p>
            </div>
          )}

          {!(entityType === 'GENERAL_TASK' || entityType === 'PROJECT_TASK') && (
            <div className="mb-4">
              <p className="text-gray-700">
                <span className="font-medium">Task Type:</span> {taskType}
              </p>
            </div>
          )}

          {/* ✅ Comments textarea */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Comments</label>
            <textarea
              name="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="w-full border px-3 py-2 rounded"
              rows={4}
              placeholder="Enter assignment instructions or details..."
            />
          </div>

          <div className="relative mb-4">
            <input
              type="text"
              className="w-full border rounded-xl p-2 pr-10 focus:outline-none focus:ring focus:border-blue-400"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button onClick={clearSearch} className="absolute top-2 right-3 text-gray-400 hover:text-red-500">
                <FaTimes />
              </button>
            )}
          </div>

          <div className="max-h-40 overflow-y-auto mb-4 border rounded-xl">
            {users.map(user => (
              <div
                key={user.id}
                className={`flex items-center justify-between py-2 px-3 border-b cursor-pointer hover:bg-gray-100 ${
                  isUserSelected(user) ? 'bg-blue-50' : ''
                }`}
                onClick={() => toggleUser({ ...user, assignmentId: '' })}
              >
                <span className="text-gray-800">
                  {user.firstName} {user.lastName} ({user.username}) - {user.email}
                </span>
                {isUserSelected(user) ? (
                  <FaUserMinus className="text-red-500" />
                ) : (
                  <FaUserPlus className="text-green-500" />
                )}
              </div>
            ))}
          </div>

          {selectedUsers.length > 0 && (
            <div className="mb-4">
              <h3 className="text-gray-700 font-medium mb-2">Selected Users</h3>
              <table className="w-full text-sm border rounded-xl overflow-hidden">
                <thead className="bg-gray-100 text-left">
                  <tr>
                    <th className="p-2">#</th>
                    <th className="p-2">First Name</th>
                    <th className="p-2">Last Name</th>
                    <th className="p-2">Username</th>
                    <th className="p-2">Email</th>
                    <th className="p-2">Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedUsers.map((user, index) => (
                    <tr key={user.id} className="border-t hover:bg-gray-50">
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2">{user.firstName}</td>
                      <td className="p-2">{user.lastName}</td>
                      <td className="p-2">{user.username}</td>
                      <td className="p-2">{user.email}</td>
                      <td className="p-2">
                        <button className="text-red-500 hover:text-red-700" onClick={() => toggleUser(user)}>
                          <FaTimes />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-between items-center mt-4">
            <button
              onClick={handleClearSelection}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-red-600 transition duration-200"
            >
              <FontAwesomeIcon icon={faBroom} className="text-red-500" />
              <span>Clear Selection</span>
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="flex items-center gap-2 bg-gray-100 text-gray-700 font-semibold px-4 py-2 rounded-xl shadow hover:bg-gray-200 hover:text-gray-900 transition duration-200"
              >
                <FontAwesomeIcon icon={faTimesIcon} className="text-gray-600" />
                <span>Cancel</span>
              </button>

              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 bg-blue-600 text-white font-semibold px-4 py-2 rounded-xl shadow hover:bg-blue-700 transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FontAwesomeIcon icon={faUserPlusIcon} className="text-white" />
                <span>Assign Selected</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};